import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { User } from '@rossum/api-client/users';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { CircularProgress, Stack } from '@rossum/ui/material';
import { useGetUser } from '../../business/users/useGetUser';
import { usePatchUser } from '../../business/users/usePatchUser';
import { getRoleName } from '../../redux/modules/groups/helpers';
import { groupsSelector } from '../../redux/modules/groups/selectors';
import { useUser } from '../user-panel/hooks/useUser';
import { Toggle } from './components/Toggle';
import { UserHeader } from './components/UserHeader';
import { getActiveUserPage } from './helpers';

type UserProps = {
  userId: number;
  renderChildren: (user: User) => ReactNode;
};

export const UserPageWrapper = ({ renderChildren, userId }: UserProps) => {
  const location = useLocation();

  const activePage = getActiveUserPage(location.pathname);

  const groups = useSelector(groupsSelector);

  const { data: loggedInUser } = useUser();
  const { data: user } = useGetUser({
    userId: userId.toString(),
  });

  const { mutate: patchUser } = usePatchUser();

  const onToggleUserClick = () =>
    user
      ? patchUser({
          userId: user.id,
          payload: { isActive: !user.isActive },
        })
      : undefined;

  const loggedInUserId = loggedInUser?.id;

  const userRole = getRoleName(user?.groups ?? [], groups);

  return (
    <PageLayout
      renderHeader={params =>
        activePage && user ? (
          <UserHeader
            {...params}
            pageKey={activePage}
            user={user}
            toggle={
              <Toggle
                key={`user-toggle-${user.id}`}
                disabled={
                  loggedInUserId === user.id ||
                  userRole === 'organization_group_admin'
                }
                active={user.isActive}
                onToggle={onToggleUserClick}
                isUserOrgGroupAdmin={userRole === 'organization_group_admin'}
              />
            }
          />
        ) : null
      }
    >
      <Stack px={4} py={4}>
        {user ? (
          renderChildren(user)
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: '200px' }}
          >
            <CircularProgress />
          </Stack>
        )}
      </Stack>
    </PageLayout>
  );
};
